import { CssBaseline, useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as dotenv from 'dotenv';
import * as path from 'path';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { store } from './app/store';
import './index.css';

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    gradient: Palette['primary'];
  }
  interface PaletteOptions {
    gradient: PaletteOptions['primary'];
  }
}

const env = process.env.NODE_ENV;
if (env === 'development') {
  dotenv.config({ path: path.resolve(process.cwd(), '.env.local') });
}

export interface ColorModeContextType {
  toggleColorMode: () => void;
  selectColorMode: (theme: 'light' | 'dark' | 'system') => void;
  mode: 'light' | 'dark';
  currentTheme: 'light' | 'dark' | 'system';
}

export const ColorModeContext = React.createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  selectColorMode: (theme: 'light' | 'dark' | 'system') => {},
  mode: 'light',
  currentTheme: 'light'
});

export function getCurrentTheme() {
  const theme = localStorage.getItem('theme');
  if (theme === 'light') return 'light';
  if (theme === 'dark') return 'dark';
  return 'system';
}

function AppWithTheme() {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [currentTheme, setCurrentTheme] = React.useState<
    'light' | 'dark' | 'system'
  >(getCurrentTheme());
  const [mode, setMode] = React.useState<'light' | 'dark'>(
    prefersDarkMode ? 'dark' : 'light'
  );

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('theme', newMode);
          return newMode;
        });
      },
      selectColorMode: (theme: 'light' | 'dark' | 'system') => {
        setCurrentTheme(theme);
        // No need to use setMode because when currentTheme was changed, mode will be changed at useEffect below.
        if (theme === 'system') {
          localStorage.removeItem('theme');
        } else {
          localStorage.setItem('theme', theme);
        }
      }
    }),
    []
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                // palette values for light mode
                primary: {
                  main: '#1877f2',
                  contrastText: '#ffffff'
                },
                secondary: {
                  main: '#eb0014',
                  contrastText: '#ffffff'
                },
                error: {
                  main: '#fa3c3c',
                  contrastText: '#ffffff'
                },
                success: {
                  main: '#42b72a',
                  contrastText: '#ffffff'
                },
                background: {
                  default: '#f0f2f5'
                },
                text: {
                  primary: '#1c1e21',
                  disabled: '#dddddd'
                },
                gradient: {
                  main: 'linear-gradient(to right, #1877f2, #0059b2)'
                }
              }
            : {
                // palette values for dark mode
                primary: {
                  main: '#66b2ff',
                  contrastText: '#161b22'
                },
                secondary: {
                  main: '#eb0014',
                  contrastText: '#fafafa'
                },
                error: {
                  main: '#fa3c3c',
                  contrastText: '#fafafa'
                },
                success: {
                  main: '#42b72a',
                  contrastText: '#fafafa'
                },
                background: {
                  default: '#0d1117',
                  paper: '#161b22'
                },
                text: {
                  primary: '#fafafa',
                  disabled: 'rgba(250, 250, 250, 0.5)'
                },
                gradient: {
                  main: '#66b2ff'
                }
              })
        },
        typography: {
          fontFamily: 'Prompt'
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920
          }
        },
        components: {
          MuiButton: {
            defaultProps: {
              style: {
                borderRadius: Number.MAX_VALUE
              }
            },
            styleOverrides: {
              sizeSmall: {
                padding: '4px 10px'
              },
              sizeMedium: {
                padding: '6px 16px'
              },
              sizeLarge: {
                padding: '8px 22px'
              }
            }
          },
          MuiDialog: {
            defaultProps: {
              style: {
                backdropFilter: 'blur(2px)'
              }
            },
            styleOverrides: {
              paper: {
                borderRadius: '16px'
              }
            }
          }
        }
      }),
    [mode]
  );

  React.useEffect(() => {
    if (currentTheme !== 'system') {
      setMode(currentTheme === 'light' ? 'light' : 'dark');
    } else {
      setMode(prefersDarkMode ? 'dark' : 'light');
    }
  }, [prefersDarkMode, currentTheme]);

  return (
    <ColorModeContext.Provider value={{ ...colorMode, mode, currentTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

ReactDOM.render(
  <BrowserRouter basename={process.env.PUBLIC_URL}>
    <Provider store={store}>
      <AppWithTheme />
    </Provider>
  </BrowserRouter>,
  document.getElementById('root')
);
